import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Loader2,
  Users,
  Search,
  UserCheck,
  UserX
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function RxImageUpload({ user, kind, onChanged }) {
  const [url, setUrl] = useState(user?.[`rx_${kind}_url`] || '');
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('kind', kind);
      fd.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/admin/users/${user.id}/rx-image`, fd, { withCredentials: true });
      setUrl(data.url);
      toast.success(`${kind === 'header' ? 'Header' : 'Footer'} image uploaded`);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); e.target.value = ''; }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API_URL}/api/admin/users/${user.id}/rx-image?kind=${kind}`, { withCredentials: true });
      setUrl('');
      toast.success('Image removed');
      onChanged?.();
    } catch { toast.error('Failed to remove'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-1.5">
      <Label className="capitalize">Prescription {kind} image</Label>
      <div className="flex items-center gap-2">
        {url ? (
          <img src={`${API_URL}${url}`} alt={`rx ${kind}`} className="h-10 max-w-[160px] object-contain rounded border border-slate-200 bg-white" data-testid={`rx-${kind}-preview`} />
        ) : (
          <span className="text-[11px] text-muted-foreground">None uploaded</span>
        )}
        <label className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-full border border-slate-200 cursor-pointer hover:bg-slate-50">
          {busy ? 'Working…' : (url ? 'Replace' : 'Upload')}
          <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={busy} data-testid={`rx-${kind}-file-input`} />
        </label>
        {url && (
          <button type="button" onClick={remove} disabled={busy} className="text-[11px] text-red-500 hover:underline" data-testid={`rx-${kind}-remove`}>Remove</button>
        )}
      </div>
    </div>
  );
}

const roleOptions = [
  { value: 'admin', label: 'Admin' },
  { value: 'doctor', label: 'Doctor' },
  { value: 'counsellor', label: 'Counsellor' },
];

const roleColors = {
  admin: 'bg-primary/10 text-primary border-primary/20',
  doctor: 'bg-info/10 text-info border-info/20',
  counsellor: 'bg-secondary/10 text-secondary border-secondary/20',
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: '',
    role: 'doctor',
    password: '',
    is_active: true,
  });

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/users`, { withCredentials: true });
      setUsers(data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      phone: '',
      role: 'doctor',
      password: '',
      is_active: true,
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/admin/users`, formData, { withCredentials: true });
      toast.success('User created successfully');
      setIsCreateOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }
      await axios.put(`${API_URL}/api/admin/users/${selectedUser.id}`, updateData, { withCredentials: true });
      toast.success('User updated successfully');
      setIsEditOpen(false);
      setSelectedUser(null);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to update user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      await axios.delete(`${API_URL}/api/admin/users/${selectedUser.id}`, { withCredentials: true });
      toast.success('User deleted successfully');
      setIsDeleteOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to delete user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      phone: user.phone || '',
      role: user.role,
      password: '',
      is_active: user.is_active,
      degree: user.degree || '',
      registration_number: user.registration_number || '',
      clinic_name: user.clinic_name || '',
    });
    setIsEditOpen(true);
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setIsDeleteOpen(true);
  };

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="users-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="user-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-medium tracking-tight text-foreground">
            User Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Create, edit, and manage user accounts
          </p>
        </div>
        <Button 
          onClick={() => { resetForm(); setIsCreateOpen(true); }}
          className="bg-primary hover:bg-primary/90 rounded-full"
          data-testid="create-user-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold">{users.length}</p>
              <p className="text-xs text-muted-foreground">Total Users</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-success/10">
              <UserCheck className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold">{users.filter(u => u.is_active).length}</p>
              <p className="text-xs text-muted-foreground">Active</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-info/10">
              <Users className="w-5 h-5 text-info" />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold">{users.filter(u => u.role === 'doctor').length}</p>
              <p className="text-xs text-muted-foreground">Doctors</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-destructive/10">
              <UserX className="w-5 h-5 text-destructive" />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold">{users.filter(u => !u.is_active).length}</p>
              <p className="text-xs text-muted-foreground">Inactive</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card className="border-border shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="font-heading text-lg">All Users</CardTitle>
              <CardDescription>Manage clinic staff accounts</CardDescription>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 rounded-xl"
                data-testid="search-users-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Desktop table */}
          <div className="rounded-xl border border-border overflow-hidden hidden sm:block">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id} className="table-row-hover" data-testid={`user-row-${user.id}`}>
                      <TableCell className="font-medium">{user.name}</TableCell>
                      <TableCell className="text-muted-foreground">{user.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`${roleColors[user.role]} capitalize`}>
                          {user.role.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.is_active ? 'default' : 'secondary'} className={user.is_active ? 'bg-success/10 text-success border-success/20' : ''}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="ghost" size="icon" onClick={() => openEditModal(user)} className="h-8 w-8" data-testid={`edit-user-${user.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openDeleteModal(user)} className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`delete-user-${user.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {/* Mobile card list */}
          <div className="sm:hidden space-y-2">
            {filteredUsers.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground text-sm">No users found</p>
            ) : (
              filteredUsers.map((user) => (
                <div key={user.id} className="p-3 rounded-xl border border-border bg-white" data-testid={`user-card-${user.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{user.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <Button variant="ghost" size="icon" onClick={() => openEditModal(user)} className="h-8 w-8" data-testid={`edit-user-m-${user.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openDeleteModal(user)} className="h-8 w-8 text-destructive" data-testid={`delete-user-m-${user.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="outline" className={`${roleColors[user.role]} capitalize text-[10px]`}>{user.role.replace('_', ' ')}</Badge>
                    <Badge variant={user.is_active ? 'default' : 'secondary'} className={`text-[10px] ${user.is_active ? 'bg-success/10 text-success border-success/20' : ''}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    {user.phone && <span className="text-[10px] text-muted-foreground">{user.phone}</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create User Modal */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-md" data-testid="create-user-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Create New User</DialogTitle>
            <DialogDescription>Add a new staff member to the clinic</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="create-name">Full Name</Label>
              <Input
                id="create-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="rounded-xl"
                data-testid="create-user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-email">Email</Label>
              <Input
                id="create-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="rounded-xl"
                data-testid="create-user-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-phone">Phone (for OTP login)</Label>
              <Input
                id="create-phone"
                type="tel"
                placeholder="10-digit phone number"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                maxLength={10}
                className="rounded-xl"
                data-testid="create-user-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-role">Role</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger className="rounded-xl" data-testid="create-user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-password">Password</Label>
              <Input
                id="create-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
                className="rounded-xl"
                data-testid="create-user-password-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)} className="rounded-full">
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} className="bg-primary hover:bg-primary/90 rounded-full" data-testid="create-user-submit">
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create User
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-md" data-testid="edit-user-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit User</DialogTitle>
            <DialogDescription>Update user information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Full Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="rounded-xl"
                data-testid="edit-user-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="rounded-xl"
                data-testid="edit-user-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-phone">Phone (for OTP login)</Label>
              <Input
                id="edit-phone"
                type="tel"
                placeholder="10-digit phone number"
                value={formData.phone || ''}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                maxLength={10}
                className="rounded-xl"
                data-testid="edit-user-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role">Role</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger className="rounded-xl" data-testid="edit-user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="border-t border-slate-200 pt-3 space-y-3" data-testid="rx-details-section">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Prescription Details (for doctors)</p>
              <div className="space-y-2">
                <Label htmlFor="edit-degree">Qualifications / Degree</Label>
                <Input id="edit-degree" placeholder="e.g. MBBS, MD (Dermatology)" value={formData.degree || ''}
                  onChange={(e) => setFormData({ ...formData, degree: e.target.value })} className="rounded-xl" data-testid="edit-user-degree-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-regno">Registration Number</Label>
                <Input id="edit-regno" placeholder="e.g. PMC/12345" value={formData.registration_number || ''}
                  onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })} className="rounded-xl" data-testid="edit-user-regno-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-clinic">Clinic / Designation line</Label>
                <Input id="edit-clinic" placeholder="e.g. Consultant Dermatologist, Eden Skin Clinic" value={formData.clinic_name || ''}
                  onChange={(e) => setFormData({ ...formData, clinic_name: e.target.value })} className="rounded-xl" data-testid="edit-user-clinic-input" />
              </div>
              <RxImageUpload user={selectedUser} kind="header" onChanged={fetchUsers} />
              <RxImageUpload user={selectedUser} kind="footer" onChanged={fetchUsers} />
              <p className="text-[10px] text-muted-foreground">Header image replaces the clinic/doctor text header on the prescription PDF; footer image prints at the bottom. Both are skipped in Print mode (pre-printed stationery).</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-password">New Password (leave blank to keep current)</Label>
              <Input
                id="edit-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                minLength={6}
                className="rounded-xl"
                data-testid="edit-user-password-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="edit-active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded"
                data-testid="edit-user-active-checkbox"
              />
              <Label htmlFor="edit-active">Account Active</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)} className="rounded-full">
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} className="bg-primary hover:bg-primary/90 rounded-full" data-testid="edit-user-submit">
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent data-testid="delete-user-modal">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedUser?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isSubmitting}
              className="bg-destructive hover:bg-destructive/90 rounded-full"
              data-testid="confirm-delete-user"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
